/**
 * BUG-032 — vector-edit surface.
 *
 * Renders the figure's traced SVG inline and lets the drafter
 * click-to-select + Delete-to-remove individual top-level
 * elements (paths/groups). Persists every deletion via
 * ``PATCH /figures/{id}/svg`` so the next page load reflects the
 * edit. Aim per the cue: small-edit deletions of stray traced
 * paths or letterforms — not full path-anchor editing.
 *
 * Approach:
 *   - Fetch the SVG once on figure-id change.
 *   - Parse with DOMParser, stamp each direct child of the root
 *     <svg> with a ``data-svg-edit-id`` attribute so React can
 *     reference + highlight them stably across re-renders.
 *   - Render via dangerouslySetInnerHTML. Click handler walks up
 *     to the nearest data-svg-edit-id ancestor. Selected element
 *     gains a blue outline via two attribute overrides applied
 *     in a useEffect (stroke + stroke-width).
 *   - Delete / Backspace removes the selected element from the
 *     in-DOM SVG, then serialises a clone (with transient
 *     attributes stripped) and PATCHes the backend.
 *   - Out of scope here: anchor-level path edits, undo/redo
 *     across deletions, multi-select. Drafter feedback drives
 *     the next slice.
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Loader2, AlertTriangle, Trash2 } from 'lucide-react';
// SEC-F01 (Phase 25 3-C) — DOMPurify wraps SVG bytes before
// dangerouslySetInnerHTML. Even though the annotated SVG is
// generated by the server-side ``_annotateSvg`` from
// server-trusted bytes (per the comment at the render site
// below), the source raster pipeline accepts user uploads —
// if any upstream step ever leaked unsanitised bytes through,
// this layer would render <script>/onload/onerror. Defence in
// depth: sanitise at every dangerouslySetInnerHTML site.
import DOMPurify from 'dompurify';
import {
  getDrawingFigureSvg,
  patchDrawingFigureSvg,
  type DrawingFigureSummary,
} from '@/api/client';

interface Props {
  matterId: string;
  figure: DrawingFigureSummary;
  zoomLevel: number;
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export default function VectorEditSurface({
  matterId, figure, zoomLevel,
}: Props): JSX.Element {
  const [svgHtml, setSvgHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // ── Load + annotate on figure change ────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSvgHtml(null);
    setSelectedKey(null);
    (async () => {
      try {
        const text = await getDrawingFigureSvg(matterId, figure.id);
        if (cancelled) return;
        setSvgHtml(_annotateSvg(text));
        setLoading(false);
      } catch (e: unknown) {
        if (cancelled) return;
        setLoadError(
          (e as { message?: string })?.message
            || 'Failed to load SVG.',
        );
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // ``figure.updated_at`` widens the dep array so the SVG re-
    // fetches whenever the row changes server-side — most
    // notably after a re-vectorize, where ``figure.id`` stays
    // the same but the persisted SVG bytes have been replaced.
    // Without this, clicking re-vectorize while the vector view
    // is open showed the stale SVG until the drafter switched
    // figures (manual remount). Backend bumps updated_at on
    // every /vectorize success (drawing.py:4937 + :4822), so
    // this re-fetches deterministically. Benefits Canvas.tsx as
    // well as KonvaCanvas — same component, same latent bug.
  }, [matterId, figure.id, figure.updated_at]);

  // ── Highlight the selected element via attribute overrides.
  //    Saved as a useEffect so the highlight survives re-renders;
  //    the original stroke is restored when selection clears. */
  useEffect(() => {
    const root = containerRef.current?.querySelector('svg');
    if (!root) return;
    root.querySelectorAll('[data-svg-edit-id]').forEach((el) => {
      const isSel = el.getAttribute('data-svg-edit-id') === selectedKey;
      // Capture the original stroke once so we can put it back when
      // the element is deselected. ``data-orig-stroke`` doubles as
      // a "we've touched this" sentinel.
      if (isSel && !el.hasAttribute('data-orig-stroke')) {
        el.setAttribute(
          'data-orig-stroke',
          el.getAttribute('stroke') ?? '__none__',
        );
        el.setAttribute(
          'data-orig-stroke-width',
          el.getAttribute('stroke-width') ?? '__none__',
        );
      }
      if (isSel) {
        el.setAttribute('stroke', '#002FA7');
        el.setAttribute('stroke-width', '2');
        (el as SVGElement).style.cursor = 'pointer';
      } else if (el.hasAttribute('data-orig-stroke')) {
        const orig = el.getAttribute('data-orig-stroke') || '__none__';
        const origW = el.getAttribute('data-orig-stroke-width') || '__none__';
        if (orig === '__none__') el.removeAttribute('stroke');
        else el.setAttribute('stroke', orig);
        if (origW === '__none__') el.removeAttribute('stroke-width');
        else el.setAttribute('stroke-width', origW);
        el.removeAttribute('data-orig-stroke');
        el.removeAttribute('data-orig-stroke-width');
      }
    });
  }, [selectedKey, svgHtml]);

  // ── Click → walk up to the closest direct child of <svg> root.
  const onClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const root = containerRef.current?.querySelector('svg');
    if (!root) return;
    let el: Element | null = e.target as Element;
    // Walk up until we hit an annotated element or escape the root.
    while (el && el !== root) {
      if (el.hasAttribute && el.hasAttribute('data-svg-edit-id')) {
        const k = el.getAttribute('data-svg-edit-id');
        setSelectedKey(k);
        return;
      }
      el = el.parentElement;
    }
    setSelectedKey(null);
  }, []);

  // ── Delete handler — removes the selected element + persists.
  const onDeleteSelected = useCallback(async () => {
    if (!selectedKey) return;
    const root = containerRef.current?.querySelector('svg');
    if (!root) return;
    const target = root.querySelector(
      `[data-svg-edit-id="${selectedKey}"]`,
    );
    if (!target) return;
    target.remove();
    setSelectedKey(null);

    // Serialise a CLONE so we can scrub the editor-only attributes
    // without affecting the live DOM (the next selection still
    // needs ``data-svg-edit-id`` to resolve).
    const clone = root.cloneNode(true) as SVGElement;
    clone.querySelectorAll('[data-svg-edit-id]').forEach((el) => {
      el.removeAttribute('data-svg-edit-id');
      el.removeAttribute('data-orig-stroke');
      el.removeAttribute('data-orig-stroke-width');
    });
    const xml = new XMLSerializer().serializeToString(clone);

    try {
      setSaveStatus('saving');
      setSaveError(null);
      await patchDrawingFigureSvg(matterId, figure.id, xml);
      setSaveStatus('saved');
      window.setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (e: unknown) {
      setSaveStatus('error');
      setSaveError(
        (e as { message?: string })?.message || 'Save failed.',
      );
    }
  }, [selectedKey, matterId, figure.id]);

  // Keyboard shortcut: Delete / Backspace removes the selected
  // element. Listener bound to window since the editor isn't
  // necessarily focused (svg children aren't keyboard targets).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (!selectedKey) return;
      // Don't fire while a text input has focus.
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      e.preventDefault();
      void onDeleteSelected();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedKey, onDeleteSelected]);

  // Sizing matches the raster surface so toggling between vector /
  // raster doesn't reflow the centre column.
  const renderW = (figure.width || 800) * zoomLevel;
  const renderH = (figure.height || 600) * zoomLevel;

  if (loading) {
    return (
      <div
        className="flex items-center justify-center rounded-sm border border-zinc-300 bg-white text-xs text-zinc-500 shadow-sm"
        style={{ width: renderW, height: renderH }}
        data-testid="vector-edit-loading"
      >
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading SVG…
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-2 rounded-sm border border-amber-300 bg-amber-50 px-4 text-xs text-amber-800 shadow-sm"
        style={{ width: renderW, height: renderH }}
        data-testid="vector-edit-error"
      >
        <AlertTriangle className="h-5 w-5" />
        <p className="text-center">{loadError}</p>
      </div>
    );
  }

  return (
    <div
      className="relative rounded-sm border border-zinc-300 bg-white shadow-sm"
      style={{ width: renderW, height: renderH }}
      data-testid="vector-edit-surface"
    >
      {/* Inlined SVG. Width/height honour the same zoomLevel +
          intrinsic dims as the raster path so chip placements stay
          consistent if the drafter toggles back to raster view. */}
      <div
        ref={containerRef}
        onClick={onClick}
        className="absolute inset-0 [&>svg]:h-full [&>svg]:w-full"
        // dangerouslySetInnerHTML is the cheapest way to mount an
        // arbitrary SVG sub-tree React doesn't have to virtual-DOM.
        // SEC-F01 (Phase 25 3-C) — sanitised via DOMPurify
        // (svg + svgFilters profiles) so any embedded
        // <script>/on*/javascript: payload is stripped before
        // rendering. Defence in depth — the annotated SVG today
        // comes from server-trusted ``_annotateSvg`` output, but
        // a future upstream change could leak unsanitised bytes
        // through; sanitising at the render site closes the
        // class of bug rather than depending on every producer.
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(svgHtml || '', {
            USE_PROFILES: { svg: true, svgFilters: true },
          }) as unknown as string,
        }}
      />
      {/* Floating action chrome — Delete button + save status. */}
      <div
        className="absolute bottom-2 right-2 flex items-center gap-2 rounded-sm bg-white/95 px-2 py-1 text-[11px] shadow ring-1 ring-zinc-200"
        data-testid="vector-edit-chrome"
      >
        <button
          type="button"
          className="inline-flex h-6 items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!selectedKey}
          onClick={() => { void onDeleteSelected(); }}
          title={selectedKey
            ? 'Delete the selected element (Del / Backspace)'
            : 'Click an element to select, then delete.'}
          data-testid="vector-edit-delete"
        >
          <Trash2 className="h-3 w-3" />
          Delete
        </button>
        <span
          className={[
            'tabular-nums',
            saveStatus === 'error' ? 'text-red-700' : 'text-zinc-500',
          ].join(' ')}
          data-testid="vector-edit-save-status"
        >
          {saveStatus === 'saving' && '○ Saving…'}
          {saveStatus === 'saved' && '○ Saved'}
          {saveStatus === 'error' && (saveError || 'Save failed')}
          {saveStatus === 'idle' && (selectedKey ? 'Selected' : 'Click an element to select')}
        </span>
      </div>
    </div>
  );
}

/** Selectable leaf elements — every visible primitive a drafter
 * might want to delete. Excludes container/infrastructure tags
 * (svg/g/defs/style/etc.) since selecting those would either
 * be a no-op (defs) or take out half the figure (top-level g
 * around the whole trace). Potrace + vtracer both wrap output
 * in nested <g>s, so stamping only direct children would let
 * the user delete only "everything" — not what we want.
 *
 * The walker recurses into <g> containers but stamps leaf shapes;
 * <text> is included because the AI-gen path can produce it (and
 * future Tesseract-OCR re-text would land here too). */
const _SELECTABLE_TAGS = new Set([
  'path', 'rect', 'circle', 'ellipse',
  'line', 'polyline', 'polygon', 'text',
]);
const _SKIP_DESCEND_TAGS = new Set([
  'defs', 'style', 'title', 'desc', 'metadata',
  'clippath', 'mask', 'filter', 'lineargradient', 'radialgradient',
]);

function _annotateSvg(svgText: string): string {
  if (!svgText || !svgText.trim()) return svgText;
  try {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    const root = doc.documentElement;
    if (!root || root.nodeName.toLowerCase() !== 'svg') return svgText;

    // pointer-events:all on the root <svg> ensures clicks reach
    // children even when they sit inside a parent <g> with no
    // explicit fill (potrace usually emits ``fill: black; stroke:
    // none`` so ``visiblePainted`` would still hit the painted
    // areas, but rasters with thin lines benefit from the more
    // permissive setting too).
    root.setAttribute('pointer-events', 'all');

    let i = 0;
    const walk = (el: Element): void => {
      const tag = el.tagName.toLowerCase();
      if (_SKIP_DESCEND_TAGS.has(tag)) return;
      if (_SELECTABLE_TAGS.has(tag)) {
        el.setAttribute('data-svg-edit-id', `el-${i++}`);
        // Make the cursor + hit area discoverable. The hover style
        // is applied by the surface's CSS rule (see component
        // render).
        (el as SVGElement).style.cursor = 'pointer';
        return;
      }
      // Containers (<g>, <a>, <switch>) — recurse.
      Array.from(el.children).forEach(walk);
    };
    Array.from(root.children).forEach(walk);
    return new XMLSerializer().serializeToString(doc);
  } catch {
    return svgText;
  }
}
